import React, { useState, useMemo } from "react";
import PendingInventoryDrawer from "../../AdminInventoryDashboard/PendingInventoryDrawer";

const RequestedInventories = ({ requestedInventories = [], employeeData }) => {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleSearchChange = (e) => setSearch(e.target.value);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return requestedInventories;

    return requestedInventories.filter((inv) => {
      const name = inv.catName?.toLowerCase() || "";
      const id = inv.categoryId?.toLowerCase() || "";
      return name.includes(term) || id.includes(term);
    });
  }, [requestedInventories, search]);

  // Empty state
  if (!requestedInventories || requestedInventories.length === 0) {
    return (
      <div className="w-full flex items-center justify-center py-16 bg-gray-50">
        <div className="max-w-md text-center bg-white rounded-2xl shadow border border-gray-200 px-6 py-8">
          <p className="text-sm text-gray-500 mb-2">Requested Inventory</p>
          <p className="text-lg font-semibold text-gray-900">
            No requested inventory items available for{" "}
            <span className="text-[#7F58E6]">
              {employeeData?.fullName || "this employee"}
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 bg-gray-50">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Requested Inventories
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {employeeData?.fullName || "Employee"}
            </p>
          </div>
          <div className="mt-3 sm:mt-0 text-sm text-gray-500 flex flex-col items-start sm:items-end">
            <span className="font-medium">
              {requestedInventories.length} request
              {requestedInventories.length > 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* Search bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="w-full sm:w-72">
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by name or ID..."
                className="w-full px-3 py-2 text-sm rounded-xl bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7F58E6]"
              />
            </div>
          </div>

          {/* Cards */}
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-8">
              No requested inventory items match the current search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filtered.map((inventory) => (
                <div
                  key={inventory.categoryId}
                  onClick={() => {
                    setSelectedCategory(inventory);
                    setIsDrawerOpen(true);
                  }}
                  className="border border-gray-200 rounded-xl p-4 sm:p-5 flex flex-col gap-3 bg-white cursor-pointer hover:bg-gray-50 transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {inventory.catName || "Unnamed Category"}
                      </p>
                      <p className="text-xs text-gray-500">
                        Pending Quantity: {inventory.count}
                      </p>
                    </div>

                    {/* Optional status chip if you have inventory.status */}
                    {inventory.isScanRequired !== undefined && (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full border ${inventory.isScanRequired ? "border-red-300 bg-red-50 text-red-700" : "border-green-300 bg-green-50 text-green-700"} text-xs font-medium`}>
                        {inventory.isScanRequired ? "Scan Required" : "No Scan"}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <PendingInventoryDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        categoryId={selectedCategory?.categoryId}
        categoryName={selectedCategory?.catName}
        employeeId={employeeData?.id}
        status="Pending"
      />
    </div>
  );
};

export default RequestedInventories;
